import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatMoney } from './format';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '18px 20px 12px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        margin: '16px 0 20px',
    },
    heading: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 8,
    },
    title: {
        margin: 0,
        fontSize: 18,
        fontWeight: 700,
        color: '#1C375D',
    },
    note: {
        margin: 0,
        fontSize: 13,
        color: '#8a92a3',
    },
    empty: {
        padding: '28px 0 20px',
        color: '#8a92a3',
        fontSize: 14,
    },
    chart: {
        height: 280,
        position: 'relative',
    },
}));

export default function SpendTrendChart({ daily, currency, mixedCurrency }) {
    const classes = useStyles();
    const currencies = useMemo(() => {
        const codes = new Set(daily.map((row) => row.currency).filter(Boolean));
        return [...codes];
    }, [daily]);
    const chartCurrency = currencies.length === 1 ? currencies[0] : currency;
    const blocked = mixedCurrency || currencies.length > 1;

    const data = useMemo(() => ({
        labels: daily.map((row) => row.date),
        datasets: [
            {
                label: 'Spend',
                data: daily.map((row) => row.spend),
                borderColor: '#1C375D',
                backgroundColor: 'rgba(28, 55, 93, 0.12)',
                pointBackgroundColor: '#F47E46',
                pointBorderColor: '#F47E46',
                fill: true,
                tension: 0.25,
            },
        ],
    }), [daily]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `Spend: ${formatMoney(context.parsed.y, chartCurrency)}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: '#8a92a3', maxRotation: 0, autoSkip: true },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    color: '#8a92a3',
                    callback: (value) => formatMoney(value, chartCurrency),
                },
            },
        },
    }), [chartCurrency]);

    return (
        <section className={classes.card}>
            <div className={classes.heading}>
                <h2 className={classes.title}>Daily expenditure</h2>
                <p className={classes.note}>Spend by day.</p>
            </div>
            {blocked ? (
                <p className={classes.empty}>
                    Daily spend is not combined while this range contains more than one currency.
                </p>
            ) : daily.length === 0 ? (
                <p className={classes.empty}>
                    No daily spend for this range. Sync from Meta, or wait until the reporting API returns daily rows.
                </p>
            ) : (
                <div className={classes.chart}>
                    <Line data={data} options={options} />
                </div>
            )}
        </section>
    );
}
